/**
 * @purpose: Mengelola dashboard purchasing agar total periodik dan rincian per prioritas tampil konsisten dengan purchase-summary.
 * @called_by: views/dashboard/dashboard_purchasing.html melalui ng-controller dashboardPurchasingCtrl.
 * @deps: AngularJS app, $timeout, urlServices, toaster, Chart.js, API /api/report/purchase-dashboard dan /api/report/purchase-summary-detail.
 * @public: dashboardPurchasingCtrl.
 * @side_effects: Mengambil data dashboard purchasing, merender chart, dan membuka detail summary per prioritas/bulan.
 */
app.controller('dashboardPurchasingCtrl', function ($scope, $timeout, urlServices, toaster) {
    var totalPembelianChart = null;
    var costSavingChart = null;

    $scope.filter = {
        startDate: moment().startOf('year'),
        endDate: moment().endOf('month')
    };
    $scope.tabs = [];
    $scope.activeTab = null;
    $scope.dashboardSettings = null;
    $scope.hasLoaded = false;
    $scope.loading = false;
    $scope.lastUpdated = null;
    $scope.monthlyRows = [];
    $scope.totalPembelianCombined = [];
    $scope.overallNotes = {
        totalPembelian: 0,
        totalNaik: 0,
        totalTurun: 0,
        totalSelisih: 0
    };
    $scope.detailModal = {
        visible: false,
        loading: false,
        title: '',
        periodMonth: '',
        direction: '',
        exportUrl: '',
        data: []
    };

    function appendRequestStamp(query) {
        return query + (query ? '&' : '') + '_ts=' + new Date().getTime();
    }

    function formatCurrency(value, asAbsolute) {
        var nominal = Number(value || 0);
        if (asAbsolute) {
            nominal = Math.abs(nominal);
        }
        return 'Rp ' + nominal.toLocaleString('id-ID', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    }

    function formatCurrencyShort(value, asAbsolute) {
        var nominal = Number(value || 0);
        if (asAbsolute) {
            nominal = Math.abs(nominal);
        }
        var absoluteNominal = Math.abs(nominal);
        if (absoluteNominal >= 1000000000) {
            return 'Rp ' + (nominal / 1000000000).toFixed(1) + ' M';
        }
        if (absoluteNominal >= 1000000) {
            return 'Rp ' + (nominal / 1000000).toFixed(1) + ' Jt';
        }
        if (absoluteNominal >= 1000) {
            return 'Rp ' + (nominal / 1000).toFixed(1) + ' Rb';
        }
        return 'Rp ' + nominal.toFixed(0);
    }

    function getCostSavingColor(value, alpha) {
        var nominal = Number(value || 0);
        if (nominal > 0) {
            return alpha ? 'rgba(192, 57, 43, ' + alpha + ')' : '#c0392b';
        }
        if (nominal < 0) {
            return alpha ? 'rgba(32, 140, 87, ' + alpha + ')' : '#208c57';
        }
        return alpha ? 'rgba(57, 73, 171, ' + alpha + ')' : '#3949ab';
    }

    function destroyCharts() {
        if (totalPembelianChart) {
            totalPembelianChart.destroy();
            totalPembelianChart = null;
        }
        if (costSavingChart) {
            costSavingChart.destroy();
            costSavingChart = null;
        }
    }

    function createChartOptions(titleText, isGrouped, customOptions) {
        customOptions = customOptions || {};
        var tooltipOptions = {
            callbacks: {
                title: function (contexts) {
                    if (typeof customOptions.tooltipTitle === 'function') {
                        return customOptions.tooltipTitle(contexts);
                    }
                    return contexts && contexts.length ? contexts[0].label : '';
                },
                label: function (context) {
                    if (typeof customOptions.tooltipLabel === 'function') {
                        return customOptions.tooltipLabel(context);
                    }
                    return (context.dataset.label || '') + ': ' + formatCurrency(context.parsed.y);
                }
            }
        };

        if (customOptions.tooltipOptions) {
            Object.keys(customOptions.tooltipOptions).forEach(function (key) {
                tooltipOptions[key] = customOptions.tooltipOptions[key];
            });
        }

        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false
            },
            plugins: {
                legend: {
                    display: !!isGrouped,
                    labels: {
                        color: '#31465d',
                        boxWidth: 14,
                        padding: 18,
                        font: {
                            size: 12,
                            weight: '600'
                        }
                    }
                },
                title: {
                    display: false,
                    text: titleText || ''
                },
                tooltip: tooltipOptions
            },
            scales: {
                x: {
                    stacked: !!isGrouped,
                    grid: {
                        display: false
                    },
                    ticks: {
                        color: '#42576d',
                        font: {
                            size: 12,
                            weight: '600'
                        }
                    }
                },
                y: {
                    beginAtZero: true,
                    stacked: !!isGrouped,
                    grid: {
                        color: 'rgba(36, 52, 71, 0.12)'
                    },
                    ticks: {
                        color: '#42576d',
                        callback: function (value) {
                            if (typeof customOptions.yTickFormatter === 'function') {
                                return customOptions.yTickFormatter(value);
                            }
                            return formatCurrencyShort(value);
                        }
                    }
                }
            }
        };
    }

    function buildMonthlyRows(tab) {
        return (tab && tab.months ? tab.months : []).map(function (item) {
            return {
                key: item.key,
                label: item.labelLong || item.key,
                TotalPembelian: Number(item.TotalPembelian || 0),
                TotalNaik: Number(item.TotalNaik || 0),
                TotalTurun: Number(item.TotalTurun || 0),
                CostSavingPurchasing: Number(item.CostSavingPurchasing || 0)
            };
        });
    }

    function buildCombinedPurchaseRows(tabs) {
        var monthMap = {};
        var orderedKeys = [];

        (tabs || []).forEach(function (tab) {
            (tab.months || []).forEach(function (item) {
                if (!monthMap[item.key]) {
                    monthMap[item.key] = {
                        key: item.key,
                        labelShort: item.labelShort || item.key,
                        labelLong: item.labelLong || item.key,
                        TotalPembelian: 0
                    };
                    orderedKeys.push(item.key);
                }
                monthMap[item.key].TotalPembelian += Number(item.TotalPembelian || 0);
            });
        });

        return orderedKeys.map(function (key) {
            return monthMap[key];
        });
    }

    function buildOverallNotes(tabs) {
        return (tabs || []).reduce(function (acc, tab) {
            acc.totalPembelian += Number((tab.notes && tab.notes.totalPembelian) || 0);
            acc.totalNaik += Number((tab.notes && tab.notes.totalNaik) || 0);
            acc.totalTurun += Number((tab.notes && tab.notes.totalTurun) || 0);
            acc.totalSelisih += Number((tab.notes && tab.notes.totalSelisih) || 0);
            return acc;
        }, {
            totalPembelian: 0,
            totalNaik: 0,
            totalTurun: 0,
            totalSelisih: 0
        });
    }

    function renderCharts() {
        destroyCharts();

        if (typeof Chart === 'undefined') {
            return;
        }

        var totalPembelianSeries = $scope.totalPembelianCombined || [];
        var totalLabels = totalPembelianSeries.map(function (item) {
            return item.labelShort || item.key;
        });
        var totalPembelianData = totalPembelianSeries.map(function (item) {
            return Number(item.TotalPembelian || 0);
        });

        var activeMonths = $scope.activeTab && $scope.activeTab.months ? $scope.activeTab.months : [];
        var labels = activeMonths.map(function (item) {
            return item.labelShort || item.key;
        });
        var costSavingRawData = activeMonths.map(function (item) {
            return Number(item.CostSavingPurchasing || 0);
        });
        var costSavingData = costSavingRawData.map(function (value) {
            return Math.abs(Number(value || 0));
        });

        var totalPembelianCanvas = document.getElementById('purchaseTotalChart');
        var costSavingCanvas = document.getElementById('purchaseCostSavingChart');
        if (!totalPembelianCanvas || !costSavingCanvas) {
            return;
        }

        totalPembelianChart = new Chart(totalPembelianCanvas, {
            type: 'bar',
            data: {
                labels: totalLabels,
                datasets: [{
                    label: 'Total Pembelian',
                    data: totalPembelianData,
                    backgroundColor: 'rgba(30, 90, 153, 0.86)',
                    borderColor: '#1e5a99',
                    borderWidth: 1,
                    borderRadius: 6,
                    maxBarThickness: 42
                }]
            },
            options: createChartOptions('Total Pembelian', false)
        });

        if (!$scope.activeTab) {
            return;
        }

        costSavingChart = new Chart(costSavingCanvas, {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [{
                    label: 'Selisih',
                    data: costSavingData,
                    backgroundColor: costSavingRawData.map(function (value) {
                        return getCostSavingColor(value, 0.84);
                    }),
                    borderColor: costSavingRawData.map(function (value) {
                        return getCostSavingColor(value);
                    }),
                    borderWidth: 1,
                    borderRadius: 6,
                    maxBarThickness: 34
                }]
            },
            options: createChartOptions('Cost Saving', false, {
                yTickFormatter: function (value) {
                    return formatCurrencyShort(value, true);
                },
                tooltipTitle: function () {
                    return 'Cost Saving Purchasing ' + ($scope.activeTab ? $scope.activeTab.priority : '');
                },
                tooltipLabel: function (context) {
                    var monthItem = activeMonths[context.dataIndex] || {};
                    return [
                        'Bulan: ' + (monthItem.labelShort || context.label || '-'),
                        'Naik: ' + formatCurrency(monthItem.TotalNaik, true),
                        'Turun: ' + formatCurrency(monthItem.TotalTurun, true),
                        'Selisih: ' + formatCurrency(monthItem.CostSavingPurchasing, true)
                    ];
                },
                tooltipOptions: {
                    displayColors: false,
                    backgroundColor: '#ffffff',
                    titleColor: '#1f2d3d',
                    bodyColor: '#405366',
                    borderColor: 'rgba(31, 45, 61, 0.16)',
                    borderWidth: 1,
                    cornerRadius: 10,
                    padding: 12,
                    titleSpacing: 8,
                    bodySpacing: 6
                }
            })
        });
    }

    $scope.setActiveTab = function (tab) {
        $scope.activeTab = tab || null;
        $scope.monthlyRows = buildMonthlyRows(tab);
        $timeout(renderCharts, 0);
    };

    $scope.getSelisihTextClass = function (value) {
        var nominal = Number(value || 0);
        if (nominal > 0) {
            return 'text-red-strong';
        }
        if (nominal < 0) {
            return 'text-green-strong';
        }
        return 'text-blue-strong';
    };

    $scope.closeDetailModal = function () {
        $scope.detailModal.visible = false;
        $scope.detailModal.loading = false;
        $scope.detailModal.title = '';
        $scope.detailModal.periodMonth = '';
        $scope.detailModal.direction = '';
        $scope.detailModal.exportUrl = '';
        $scope.detailModal.data = [];
    };

    $scope.exportDetailExcel = function () {
        if ($scope.detailModal.loading || !$scope.detailModal.exportUrl || !$scope.detailModal.data.length) {
            return;
        }
        window.open($scope.detailModal.exportUrl, '_blank');
    };

    $scope.openMonthlyDetail = function (row, direction) {
        row = row || {};
        if (!$scope.activeTab) {
            return;
        }

        var nominal = direction === 'naik' ? Number(row.TotalNaik || 0) : Number(row.TotalTurun || 0);
        if (!nominal) {
            return;
        }

        var start = moment($scope.filter.startDate);
        var end = moment($scope.filter.endDate);
        var directionLabel = direction === 'naik' ? 'Naik' : 'Turun';

        $scope.detailModal.visible = true;
        $scope.detailModal.loading = true;
        $scope.detailModal.periodMonth = row.key || '';
        $scope.detailModal.direction = direction || '';
        $scope.detailModal.title = 'Detail ' + directionLabel + ' ' + $scope.activeTab.priority + ' - ' + (row.label || '-');
        $scope.detailModal.data = [];

        var query = 'start_date=' + start.format('YYYY-MM-DD') + '&end_date=' + end.format('YYYY-MM-DD');
        if (row.key) {
            query += '&period_month=' + encodeURIComponent(row.key);
        }
        query += '&direction=' + encodeURIComponent(direction || 'all');
        query += '&priority=' + encodeURIComponent($scope.activeTab.priority || '');
        query += '&context=dashboard';

        query = appendRequestStamp(query);

        $scope.detailModal.exportUrl = '/api/report/purchase-summary-detail?' + query + '&excel=1';

        urlServices.ambilData('api/report/purchase-summary-detail?' + query, {}, function (err, sukses) {
            $scope.detailModal.loading = false;
            if (err) {
                toaster.pop('error', 'Error', err);
                $scope.detailModal.exportUrl = '';
                $scope.detailModal.data = [];
                return;
            }
            $scope.detailModal.data = sukses.data || [];
        });
    };

    $scope.getPeriodLabel = function () {
        if (!$scope.filter.startDate || !$scope.filter.endDate) {
            return '-';
        }
        return moment($scope.filter.startDate).format('DD MMM YYYY') + ' s/d ' + moment($scope.filter.endDate).format('DD MMM YYYY');
    };

    $scope.loadData = function () {
        var start = moment($scope.filter.startDate);
        var end = moment($scope.filter.endDate);
        if (!start.isValid() || !end.isValid()) {
            return toaster.pop('error', 'Error', 'Periode belum valid');
        }
        if (end.isBefore(start)) {
            return toaster.pop('error', 'Error', 'Tanggal akhir tidak boleh lebih kecil dari tanggal awal');
        }

        $scope.hasLoaded = true;
        $scope.loading = true;
        destroyCharts();
        var dashboardQuery = 'start_date=' + start.format('YYYY-MM-DD') + '&end_date=' + end.format('YYYY-MM-DD');
        dashboardQuery = appendRequestStamp(dashboardQuery);
        urlServices.ambilData('api/report/purchase-dashboard?' + dashboardQuery, {}, function (err, sukses) {
            $scope.loading = false;
            if (err) {
                $scope.tabs = [];
                $scope.activeTab = null;
                $scope.dashboardSettings = null;
                $scope.monthlyRows = [];
                $scope.totalPembelianCombined = [];
                $scope.overallNotes = buildOverallNotes([]);
                $scope.closeDetailModal();
                return toaster.pop('error', 'Error', err);
            }

            $scope.tabs = sukses.tabs || [];
            $scope.dashboardSettings = sukses.settings || null;
            $scope.totalPembelianCombined = buildCombinedPurchaseRows($scope.tabs);
            $scope.overallNotes = buildOverallNotes($scope.tabs);
            $scope.lastUpdated = new Date();
            $scope.closeDetailModal();
            if ($scope.tabs.length) {
                $scope.setActiveTab($scope.tabs[0]);
            } else {
                $scope.activeTab = null;
                $scope.monthlyRows = [];
                $scope.overallNotes = buildOverallNotes([]);
                $timeout(renderCharts, 0);
            }
        });
    };

    $scope.$on('$destroy', function () {
        destroyCharts();
    });
});